import { CalendarDays, Check, Heart, Lock, Users } from "lucide-react";
import type { ReactNode } from "react";
import { AmbientCelebrationBackdrop } from "@/shared/components/ambient-celebration-backdrop";
import { StepProgressBar, type StepProgressItem } from "@/shared/components/step-progress-bar";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/cn";
import type { QuestLevel } from "./onboarding-quest.logic";

interface OnboardingQuestPanelProps {
  levels: QuestLevel[];
  welcomeMessage: string;
  onStartImport: () => void;
  onStartKudos: () => void;
  onStartCheckIn: () => void;
  onRemindLater: () => void;
}

interface QuestLevelAction {
  label: string;
  onClick: () => void;
}

const QUEST_ACTION_LABEL: Record<QuestLevel["id"], string> = {
  importTeam: "Import users",
  giveKudos: "Give kudos",
  dailyCheckIn: "Start check-in",
};

export function OnboardingQuestPanel({
  levels,
  welcomeMessage,
  onStartImport,
  onStartKudos,
  onStartCheckIn,
  onRemindLater,
}: OnboardingQuestPanelProps): ReactNode {
  const progressSteps = buildProgressSteps(levels);
  const actionForLevel = (level: QuestLevel): QuestLevelAction => {
    const onClick =
      level.id === "importTeam"
        ? onStartImport
        : level.id === "giveKudos"
          ? onStartKudos
          : onStartCheckIn;
    return { label: QUEST_ACTION_LABEL[level.id], onClick };
  };

  return (
    <div className="relative isolate flex flex-col gap-6 overflow-hidden p-6">
      <AmbientCelebrationBackdrop tone="subtle" />
      <div className="flex items-start gap-4 border-b border-border pb-6">
        <div
          aria-hidden="true"
          className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-full bg-pulse-soft shadow-brand"
        >
          <span className="size-3 rounded-full bg-pulse" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-xs font-medium tracking-wide text-text-brand md:text-sm">
            Level 1 of 3
          </p>
          <div className="flex flex-col gap-1.5">
            <h2 className="text-xl font-semibold text-text-primary">Level up your workspace</h2>
            <p id="onboarding-import-welcome" className="text-sm text-text-tertiary">
              {welcomeMessage}
            </p>
            <p id="onboarding-import-description" className="text-sm text-text-secondary">
              Bring your team in to start your workspace&apos;s pulse.
            </p>
          </div>
          <div aria-hidden="true">
            <StepProgressBar
              ariaLabel="Workspace setup progress"
              steps={progressSteps}
              className="mt-2"
              size="compact"
              shape="rounded"
              surface="subtle"
              nodeContentMode="index"
              showTooltip={false}
            />
          </div>
        </div>
      </div>

      <ol className="flex flex-col gap-3" aria-label="Workspace setup levels">
        {levels.map((level) => (
          <QuestLevelCard key={level.id} level={level} action={actionForLevel(level)} />
        ))}
      </ol>

      <div className="flex justify-end">
        <Button type="button" variant="outline" size="lg" onClick={onRemindLater}>
          Remind me later
        </Button>
      </div>
    </div>
  );
}

function buildProgressSteps(levels: ReadonlyArray<QuestLevel>): ReadonlyArray<StepProgressItem> {
  return levels.map((level, index) => ({
    id: level.id,
    label: level.label,
    detail: level.description,
    title: level.label,
    description: level.description,
    state: level.done ? "done" : level.current ? "current" : "upcoming",
    icon: level.id === "importTeam" ? Users : level.id === "giveKudos" ? Heart : CalendarDays,
    stepNumber: index + 1,
  }));
}

function QuestLevelCard({
  level,
  action,
}: {
  level: QuestLevel;
  action: QuestLevelAction;
}): ReactNode {
  const active = level.current && !level.locked;
  const done = level.done;

  return (
    <li
      aria-current={level.current ? "step" : undefined}
      className={cn(
        "rounded-xl border px-4 py-4",
        active
          ? "border-transparent bg-pulse-soft shadow-brand"
          : "border-border bg-surface-subtle",
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <span
            aria-hidden="true"
            className={cn(
              "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full ring-1 ring-inset",
              done
                ? "bg-success-bg text-success-fg ring-success-bg"
                : active
                  ? "bg-background/80 text-text-brand ring-primary/20"
                  : "bg-background text-text-tertiary ring-border-strong",
            )}
          >
            {done ? (
              <Check className="size-3.5" strokeWidth={2.5} />
            ) : level.locked ? (
              <Lock className="size-3.5" strokeWidth={2.25} />
            ) : (
              <span className="size-2 rounded-full bg-primary" />
            )}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p
                className={cn(
                  "text-sm font-medium",
                  level.locked ? "text-text-tertiary" : "text-text-primary",
                )}
              >
                {level.label}
              </p>
              {level.locked ? (
                <Badge variant="secondary" className="border-0 bg-surface-muted text-text-tertiary">
                  Locked
                </Badge>
              ) : null}
              {done ? (
                <Badge variant="secondary" className="border-0 bg-success-bg text-success-fg">
                  Done
                </Badge>
              ) : null}
            </div>
            <p
              className={cn(
                "mt-1 text-sm",
                level.locked ? "text-text-tertiary" : "text-text-secondary",
              )}
            >
              {level.description}
            </p>
          </div>
        </div>
        {active ? (
          <Button
            id="onboarding-import-primary-cta"
            type="button"
            variant="pulse"
            size="lg"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ) : null}
      </div>
    </li>
  );
}
